import { z } from "zod";

import type { DbEntity } from "../../../../client";
import { storableUrl } from "../../../../shared/core/storable-text.schema";

/**
 * Upload operations for various document sources
 * Includes: UploadFile, CreateText, AddWebsite, CreateGoogleSheet
 */

// ============================================================================
// UPLOAD FILE
// ============================================================================

export const DocumentUploadFileBodySchema = z.custom<FormData>();

// ============================================================================
// CREATE TEXT
// ============================================================================

export const DocumentCreateTextBodySchema = z.object({
  name: z.string().trim().min(1).max(255),
  content: z.string(),
  parentId: z.string().optional(),
  collectionId: z.string().optional(),
  description: z.string().optional()
});

// ============================================================================
// ADD WEBSITE
// ============================================================================

export const DocumentAddWebsiteBodySchema = z.object({
  url: storableUrl(z.string().url()),
  mode: z.enum(["sitemap", "crawl"]),
  config: z
    .object({
      urls: z.array(storableUrl(z.string())).optional(),
      max_depth: z.number().int().min(1).max(20).optional(),
      max_pages: z.number().int().min(1).max(10000).optional()
    })
    .optional(),
  parentId: z.string().optional(),
  description: z.string().optional()
});

// ============================================================================
// CREATE GOOGLE SHEET
// ============================================================================

export const DocumentCreateGoogleSheetBodySchema = z.object({
  name: z.string().trim().min(1).max(255),
  url: z.string().url(),
  parentId: z.string().optional(),
  metadata: z
    .object({
      hasHeaderRow: z.boolean().optional(),
      /**
       * Header names whose values should be embedded for semantic search.
       * When omitted or empty, every column is embedded (default behaviour).
       * When set, only the listed columns drive the embedding while retrieval
       * still returns the whole row.
       */
      embedColumns: z.array(z.string()).optional()
    })
    .optional()
});

export const DocumentCreateGoogleSheetResponseSchema = z.object({
  folder: z.custom<DbEntity.Document>(),
  sheets: z.array(z.custom<DbEntity.Document>()),
  message: z.string()
});

// ============================================================================
// GOOGLE SHEET COLUMNS (preview headers before import)
// ============================================================================

export const DocumentGoogleSheetColumnsBodySchema = z.object({
  url: z.string().url()
});

export const DocumentGoogleSheetColumnsResponseSchema = z.object({
  /** Per-tab header names. Tabs without a detectable header row return []. */
  sheets: z.array(
    z.object({
      title: z.string(),
      headers: z.array(z.string())
    })
  ),
  /** Union of all headers across tabs, de-duplicated, preserving first-seen order. */
  columns: z.array(z.string())
});
